import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import { History, AlertCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Color mapping for canonical business_status values
const STATUS_COLORS = {
  draft: "bg-slate-100 text-slate-700 border-slate-200",
  signed: "bg-blue-50 text-blue-700 border-blue-200",
  submitted: "bg-indigo-50 text-indigo-700 border-indigo-200",
  processing: "bg-amber-50 text-amber-700 border-amber-200",
  validated: "bg-emerald-50 text-emerald-700 border-emerald-200",
  active: "bg-green-100 text-green-800 border-green-300 font-semibold",
  terminated: "bg-red-50 text-red-700 border-red-200",
  cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  rejected: "bg-rose-100 text-rose-700 border-rose-300",
};

const SOURCE_LABELS = {
  salesforce: "Salesforce",
  easy: "Easy",
  crm_legacy: "CRM Legacy",
  manual: "Manual",
};

const STATUS_HINTS = {
  draft: "„draft” = niciun semnal autoritar (SF/furnizor) înregistrat încă pentru contract. Nu înseamnă că vânzarea nu a fost trimisă.",
  signed: "Contract semnat (semnal SF/Easy), încă neactivat la furnizor.",
  active: "Furnizare activă confirmată de o sursă autoritară (SF „Activat” / furnizor).",
  terminated: "Contract încheiat/anulat de furnizor sau SF.",
};

export const BusinessStatusBadge = ({ status, source, date, "data-testid": testid }) => {
  if (!status) return null;
  const cls = STATUS_COLORS[status] || STATUS_COLORS.draft;
  const sourceLabel = SOURCE_LABELS[source] || source || "?";
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge className={`${cls} border cursor-help`} data-testid={testid}>
            Business: {status}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>
          <div className="text-xs max-w-xs space-y-1">
            <div>Status canonic de <strong>business</strong> (contract.business_status) — derivat din surse autoritare (SF › furnizor › CRM › manual). Diferit de statusul intern (flux BO) și de statusul raportat de Easy (partener).</div>
            {STATUS_HINTS[status] && <div className="text-slate-500">{STATUS_HINTS[status]}</div>}
            <div>Sursă: <strong>{sourceLabel}</strong></div>
            {date && <div>Stabilit la: {new Date(date).toLocaleDateString("ro-RO")}</div>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ro-RO", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const StatusTrail = ({ contractId }) => {
  const [trail, setTrail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!contractId) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/contracts/${contractId}/status-trail`);
        setTrail(r.data);
      } catch (e) {
        setError(e.response?.data?.detail || "Nu s-a putut încărca istoricul");
      } finally {
        setLoading(false);
      }
    })();
  }, [contractId]);

  if (!contractId) return null;

  return (
    <Card data-testid="status-trail-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <History className="h-4 w-4" />Istoric cazuri procesare
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-slate-400">Se încarcă...</p>
        ) : error ? (
          <p className="text-sm text-red-500 flex items-center gap-2">
            <AlertCircle className="h-4 w-4" />{error}
          </p>
        ) : !trail || (trail.cases.length === 0 && trail.status_changes.length === 0) ? (
          <p className="text-sm text-slate-400" data-testid="status-trail-empty">
            Niciun caz de procesare înregistrat încă. Importurile Easy/Salesforce vor popula automat istoricul.
          </p>
        ) : (
          <div className="space-y-6">
            {/* Processing cases — raw per-source events */}
            {trail.cases.length > 0 && (
              <div>
                <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Cazuri brute per sursă ({trail.cases.length})
                </h4>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Sursă</TableHead>
                        <TableHead className="text-xs">Dosar</TableHead>
                        <TableHead className="text-xs">Caz</TableHead>
                        <TableHead className="text-xs">Source Status</TableHead>
                        <TableHead className="text-xs">Data</TableHead>
                        <TableHead className="text-xs">Attempt</TableHead>
                        <TableHead className="text-xs">Terminat la</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {trail.cases.map((c) => (
                        <TableRow key={c.id} data-testid={`case-row-${c.id}`}>
                          <TableCell>
                            <Badge variant="outline" className="text-[10px] uppercase">
                              {SOURCE_LABELS[c.source_system] || c.source_system}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono text-xs">{c.dossier_number || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{c.case_number || "—"}</TableCell>
                          <TableCell className="text-xs">{c.source_status || "—"}</TableCell>
                          <TableCell className="text-xs">
                            {c.source_status_date
                              ? new Date(c.source_status_date).toLocaleDateString("ro-RO")
                              : "—"}
                          </TableCell>
                          <TableCell className="text-xs text-center">
                            <Badge variant="secondary" className="text-[10px]">#{c.attempt_number}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {c.terminated_at ? (
                              <Badge className="bg-red-50 text-red-700 border-red-200 border text-[10px]">
                                {fmtDate(c.terminated_at)}
                              </Badge>
                            ) : "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}

            {/* Status changes — canonical transitions */}
            {trail.status_changes.length > 0 && (
              <div>
                <h4 className="text-xs uppercase tracking-wide text-slate-500 mb-2">
                  Tranziții business_status ({trail.status_changes.length})
                </h4>
                <ul className="space-y-2">
                  {trail.status_changes.map((s) => (
                    <li key={s.id} className="text-xs flex items-center gap-2" data-testid={`status-change-${s.id}`}>
                      <span className="text-slate-400 font-mono">{fmtDate(s.created_at)}</span>
                      <BusinessStatusBadge status={s.old_status || "draft"} source={s.old_source} />
                      <span className="text-slate-400">→</span>
                      <BusinessStatusBadge status={s.new_status} source={s.new_source} />
                      {s.raw_status && (
                        <span className="text-slate-400 italic">(raw: {s.raw_status})</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default StatusTrail;
